"use client"

import * as React from "react"
import {
  CircleDollarSign,
  Languages,
  Loader2,
  Monitor,
  Moon,
  Sun,
  SunMoon,
} from "lucide-react"
import { useTranslation } from "react-i18next"
import { useTheme } from "next-themes"

import { SearchableCombobox } from "@/components/combobox-with-states-4"
import { Badge } from "@/components/ui/badge"
import {
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from "@/components/ui/dropdown-menu"
import { useToast } from "@/components/ui/toast-provider"
import { useCurrencyExchangeRates } from "@/hooks/use-currency-exchange-rates"
import { useUserProfile } from "@/hooks/use-user-profile"
import { useAuth } from "@/lib/auth"
import {
  defaultAppTaskCurrencyFromProfile,
  isAppTaskCurrency,
  type AppTaskCurrency,
} from "@/lib/app-currency"
import { icon } from "@/lib/design-tokens"
import i18n from "@/lib/i18n"
import { cn } from "@/lib/utils"
import type { AppTheme } from "@/types/user-settings"

/**
 * Language, theme, and currency as nested menus — must render inside a DropdownMenuContent.
 */
export function ProfileMenuDisplayPrefs() {
  const { t, i18n: i18nInstance } = useTranslation()
  const { setTheme, theme } = useTheme()
  const { user } = useAuth()
  const toast = useToast()
  const { profile, loading: profileLoading, updateProfile } = useUserProfile()
  const { currencyOptions, usdPerUnit, loading: fxLoading } = useCurrencyExchangeRates()

  const [saving, setSaving] = React.useState(false)

  const currentLang = i18nInstance.language === "en" ? "en" : "pt-BR"
  const activeTheme = ((theme as AppTheme) || "system") satisfies AppTheme
  const effectiveCurrency: AppTaskCurrency = defaultAppTaskCurrencyFromProfile(profile, usdPerUnit)

  async function applyLanguage(lang: "pt-BR" | "en") {
    if (!user?.id) return
    setSaving(true)
    try {
      await i18n.changeLanguage(lang)
      const { error } = await updateProfile({ language: lang })
      if (error) toast.error(error.message)
      else toast.success(t("settings.toastPreferencesSaved"))
    } finally {
      setSaving(false)
    }
  }

  async function applyTheme(next: AppTheme) {
    setTheme(next)
    setSaving(true)
    try {
      const { error } = await updateProfile({ theme: next })
      if (error) toast.error(error.message)
      else toast.success(t("settings.toastPreferencesSaved"))
    } finally {
      setSaving(false)
    }
  }

  async function applyCurrency(code: string) {
    if (!isAppTaskCurrency(code, usdPerUnit)) return
    setSaving(true)
    try {
      const { error } = await updateProfile({ preferred_currency: code })
      if (error) toast.error(error.message)
      else toast.success(t("settings.toastPreferencesSaved"))
    } finally {
      setSaving(false)
    }
  }

  const prefsDisabled = saving || profileLoading || !user?.id

  const languageBadgeLabel = currentLang === "en" ? "EN" : "PT-BR"
  const themeBadgeLabel =
    activeTheme === "light"
      ? t("settings.themeLight")
      : activeTheme === "dark"
        ? t("settings.themeDark")
        : t("settings.themeSystem")

  return (
    <>
      <DropdownMenuSeparator />
      <DropdownMenuLabel>{t("profileMenu.displaySection")}</DropdownMenuLabel>

      <DropdownMenuSub>
        <DropdownMenuSubTrigger disabled={prefsDisabled} className="gap-1.5">
          <Languages className={cn(icon.md, "shrink-0 opacity-80")} aria-hidden />
          <span className="min-w-0 flex-1 truncate text-left">{t("settings.language")}</span>
          <Badge
            variant="secondary"
            className="h-5 shrink-0 px-1.5 font-mono text-xs font-semibold tabular-nums uppercase"
          >
            {languageBadgeLabel}
          </Badge>
        </DropdownMenuSubTrigger>
        <DropdownMenuSubContent className="min-w-[10rem]">
          <DropdownMenuRadioGroup
            value={currentLang}
            onValueChange={(v) => {
              if (v === "pt-BR" || v === "en") void applyLanguage(v)
            }}
          >
            <DropdownMenuRadioItem value="pt-BR" disabled={prefsDisabled}>
              🇧🇷 PT
            </DropdownMenuRadioItem>
            <DropdownMenuRadioItem value="en" disabled={prefsDisabled}>
              🇬🇧 EN
            </DropdownMenuRadioItem>
          </DropdownMenuRadioGroup>
        </DropdownMenuSubContent>
      </DropdownMenuSub>

      <DropdownMenuSub>
        <DropdownMenuSubTrigger disabled={prefsDisabled} className="gap-1.5">
          <SunMoon className={cn(icon.md, "shrink-0 opacity-80")} aria-hidden />
          <span className="min-w-0 flex-1 truncate text-left">{t("settings.theme")}</span>
          <Badge
            variant="secondary"
            className="h-5 max-w-[6.5rem] shrink-0 truncate px-1.5 text-xs font-semibold"
          >
            {themeBadgeLabel}
          </Badge>
        </DropdownMenuSubTrigger>
        <DropdownMenuSubContent className="min-w-[10rem]">
          <DropdownMenuRadioGroup
            value={activeTheme}
            onValueChange={(v) => {
              if (v === "light" || v === "dark" || v === "system") void applyTheme(v)
            }}
          >
            <DropdownMenuRadioItem value="light" disabled={prefsDisabled}>
              <Sun className={cn(icon.md, "opacity-80")} aria-hidden />
              {t("settings.themeLight")}
            </DropdownMenuRadioItem>
            <DropdownMenuRadioItem value="dark" disabled={prefsDisabled}>
              <Moon className={cn(icon.md, "opacity-80")} aria-hidden />
              {t("settings.themeDark")}
            </DropdownMenuRadioItem>
            <DropdownMenuRadioItem value="system" disabled={prefsDisabled}>
              <Monitor className={cn(icon.md, "opacity-80")} aria-hidden />
              {t("settings.themeSystem")}
            </DropdownMenuRadioItem>
          </DropdownMenuRadioGroup>
        </DropdownMenuSubContent>
      </DropdownMenuSub>

      <DropdownMenuSub>
        <DropdownMenuSubTrigger disabled={prefsDisabled || fxLoading} className="gap-1.5">
          <CircleDollarSign className={cn(icon.md, "shrink-0 opacity-80")} aria-hidden />
          <span className="min-w-0 flex-1 truncate text-left">{t("settings.preferredCurrency")}</span>
          <span className="flex shrink-0 items-center gap-1">
            <Badge
              variant="secondary"
              className="h-5 px-1.5 font-mono text-xs font-semibold tabular-nums uppercase"
            >
              {effectiveCurrency}
            </Badge>
            {fxLoading ? <Loader2 className="size-3.5 animate-spin opacity-60" aria-hidden /> : null}
          </span>
        </DropdownMenuSubTrigger>
        <DropdownMenuSubContent className="min-w-[min(100vw-2rem,22rem)] max-w-[min(100vw-2rem,22rem)] overflow-hidden p-0">
          {fxLoading || currencyOptions.length === 0 ? (
            <div className="flex items-center gap-2 px-3 py-4 text-sm text-muted-foreground">
              <Loader2 className="size-4 animate-spin" aria-hidden />
              {t("nav.loading")}
            </div>
          ) : (
            <SearchableCombobox
              variant="inline"
              value={effectiveCurrency}
              onValueChange={(v) => void applyCurrency(v)}
              options={currencyOptions}
              searchPlaceholder={t("commandPalette.searchPlaceholder")}
              disabled={prefsDisabled}
              commandListClassName="max-h-[min(50vh,17rem)]"
              className="rounded-none border-0 bg-popover p-1 shadow-none"
            />
          )}
        </DropdownMenuSubContent>
      </DropdownMenuSub>
    </>
  )
}
